import BotonInstructivo from "./BotonInstructivo";
import BotonMapaProceso from "./BotonMapaProceso";
import { useState, useMemo, useEffect, useCallback } from "react";
import { authHeader } from './supabase';

const STORAGE_HC      = 'alqos_huella_carbono';
const STORAGE_HC_HIST = 'alqos_huella_historial';
const STORAGE_FACTORES_CUSTOM = 'alqos_hc_factores_custom';

const storage = {
  get: (k, def={}) => { try { return JSON.parse(localStorage.getItem(k)||JSON.stringify(def)); } catch { return def; } },
  set: (k,v) => { try { localStorage.setItem(k,JSON.stringify(v)); return true; } catch { return false; } },
};

// ─── FACTORES BASE (editables por admin) ──────────────────────
const FACTORES_BASE = {
  a1: [
    { id:'gas_natural',    label:'Gas natural',              unidad:'m³',          factor:2.034,  fuente:'Secretaría de Energía AR' },
    { id:'gasoil',         label:'Gasoil (vehículos)',        unidad:'litros',       factor:2.668,  fuente:'IPCC AR5 / MADS Argentina' },
    { id:'nafta',          label:'Nafta (vehículos)',         unidad:'litros',       factor:2.310,  fuente:'IPCC AR5 / MADS Argentina' },
    { id:'glp',            label:'GLP / Gas envasado',        unidad:'kg',           factor:2.983,  fuente:'IPCC AR5' },
    { id:'diesel_gen',     label:'Diésel generador',          unidad:'litros',       factor:2.688,  fuente:'IPCC AR5' },
    { id:'otros_a1',       label:'Otros A1 (especificar)',    unidad:'unidad',       factor:0,      fuente:'Completar' },
  ],
  a2: [
    { id:'electricidad',   label:'Electricidad de red',       unidad:'kWh',          factor:0.321,  fuente:'CAMMESA 2023' },
    { id:'solar',          label:'Paneles solares',           unidad:'kWh',          factor:0,      fuente:'Energía renovable propia' },
    { id:'rec',            label:'Electricidad renovable REC', unidad:'kWh',         factor:0,      fuente:'Certificado renovable' },
    { id:'vapor',          label:'Vapor comprado',            unidad:'GJ',           factor:67.3,   fuente:'IPCC AR5' },
    { id:'otros_a2',       label:'Otros A2 (especificar)',    unidad:'kWh',          factor:0,      fuente:'Completar' },
  ],
  a3: [
    { id:'agua',           label:'Agua de red',               unidad:'m³',           factor:0.344,  fuente:'IPCC / INTA Argentina' },
    { id:'transp_prov',    label:'Transporte proveedores',    unidad:'t·km',         factor:0.096,  fuente:'IPCC AR5' },
    { id:'transp_dist',    label:'Distribución (gasoil)',     unidad:'litros',       factor:2.668,  fuente:'IPCC AR5 / MADS' },
    { id:'papel_carton',   label:'Papel y cartón consumido',  unidad:'kg',           factor:0.921,  fuente:'Ecoinvent / GHG Protocol' },
    { id:'plancha_offset', label:'Planchas offset aluminio',  unidad:'kg',           factor:8.240,  fuente:'Ecoinvent industria gráfica' },
    { id:'tintas',         label:'Tintas y barnices',         unidad:'kg',           factor:2.850,  fuente:'Ecoinvent industria gráfica' },
    { id:'residuos_relleno',label:'Residuos a relleno',      unidad:'kg',           factor:0.580,  fuente:'IPCC AR5 / CEAMSE' },
    { id:'residuos_recic', label:'Residuos reciclados',       unidad:'kg',           factor:0.021,  fuente:'IPCC AR5' },
    { id:'vuelos',         label:'Viajes aéreos',             unidad:'km·pasajero',  factor:0.255,  fuente:'ICAO Calculator' },
    { id:'otros_a3',       label:'Otros A3 (especificar)',    unidad:'unidad',       factor:0,      fuente:'Completar' },
  ],
};

// Peso estimado plancha offset 520x400mm aluminio ~0.35kg
const KG_POR_PLANCHA = 0.35;
// Benchmark industria gráfica argentina (tCO₂eq/t papel procesado)
const BENCHMARK_SECTOR = 2.1;

const ALCANCE_COLOR = { a1:'#e63946', a2:'#f59e0b', a3:'#3b82f6' };
const ALCANCE_LABEL = { a1:'Alcance 1 — Directas', a2:'Alcance 2 — Energía', a3:'Alcance 3 — Indirectas' };

function cargarFactores() {
  try {
    const custom = JSON.parse(localStorage.getItem(STORAGE_FACTORES_CUSTOM)||'null');
    if (!custom) return FACTORES_BASE;
    // Merge: mantener base + sobrescribir factores editados + agregar custom
    const merged = { a1:[...FACTORES_BASE.a1], a2:[...FACTORES_BASE.a2], a3:[...FACTORES_BASE.a3] };
    Object.entries(custom).forEach(([alcance, lista]) => {
      if (!merged[alcance]) merged[alcance] = [];
      lista.forEach(cf => {
        const idx = merged[alcance].findIndex(f=>f.id===cf.id);
        if (idx>=0) merged[alcance][idx] = cf;
        else merged[alcance].push(cf);
      });
    });
    return merged;
  } catch { return FACTORES_BASE; }
}

function calcularEmisiones(consumos, factores) {
  let total = 0;
  const por_alcance = { a1:0, a2:0, a3:0 };
  Object.entries(factores||FACTORES_BASE).forEach(([alcance, fuentes]) => {
    fuentes.forEach(f => {
      const em = Number(consumos[f.id]||0) * f.factor / 1000;
      por_alcance[alcance] = (por_alcance[alcance]||0) + em;
      total += em;
    });
  });
  return { total, por_alcance };
}

// ─── LEER DATOS AUTOMÁTICOS DE OTROS MÓDULOS ──────────────────
function leerDatosAuto() {
  const resultado = {
    papel_carton: 0,      // kg papel desde Ingreso Materiales
    plancha_offset: 0,    // kg planchas desde Pre Prensa
    tintas: 0,            // kg tintas desde Ingreso Materiales
    ots_completadas: 0,   // cantidad OTs completadas
    fuentes: [],          // detalle de qué se tomó y de dónde
  };

  // --- DESDE INGRESO DE MATERIALES ---
  try {
    const ingresos = JSON.parse(localStorage.getItem('alqos_ingreso_materiales')||'[]');
    ingresos.forEach(ing => {
      const mat = (ing.material||ing.descripcion||'').toLowerCase();
      const qty  = Number(ing.cantidad||ing.peso||0);
      const unid = (ing.unidad||'').toLowerCase();
      // Convertir a kg si viene en toneladas
      const kg = unid==='t'||unid==='ton' ? qty*1000 : qty;

      if (mat.includes('papel')||mat.includes('cartul')||mat.includes('carton')||mat.includes('cartón')) {
        resultado.papel_carton += kg;
        resultado.fuentes.push({ origen:'Ingreso Materiales', campo:'papel_carton', valor:kg, label:`${ing.material||ing.descripcion} — ${kg}kg` });
      }
      if (mat.includes('tinta')||mat.includes('barniz')||mat.includes('lacado')) {
        resultado.tintas += kg;
        resultado.fuentes.push({ origen:'Ingreso Materiales', campo:'tintas', valor:kg, label:`${ing.material||ing.descripcion} — ${kg}kg` });
      }
    });
  } catch(e) { console.warn('HC Auto: no se pudo leer ingreso_materiales', e); }

  // --- DESDE PRE PRENSA (planchas quemadas) ---
  try {
    const trabajosPP = JSON.parse(localStorage.getItem('alqos_preprensa')||'[]');
    let totalPlanchas = 0;
    trabajosPP.forEach(t => {
      // planchas = versiones del trabajo (cada versión = 1 juego de planchas CMYK = 4 planchas)
      const versiones = t.historialEstados?.filter(h=>h.estado==='en_proceso').length || 1;
      const planchas  = Number(t.cantidadPlanchas||0) || versiones * 4;
      totalPlanchas  += planchas;
    });
    resultado.plancha_offset = totalPlanchas * KG_POR_PLANCHA;
    if (totalPlanchas > 0)
      resultado.fuentes.push({ origen:'Pre Prensa', campo:'plancha_offset', valor:resultado.plancha_offset, label:`${totalPlanchas} planchas estimadas → ${resultado.plancha_offset.toFixed(2)}kg Al` });
  } catch(e) { console.warn('HC Auto: no se pudo leer preprensa', e); }

  // --- DESDE OTs ---
  try {
    const ots = JSON.parse(localStorage.getItem('alqos_ots')||'[]');
    resultado.ots_completadas = ots.filter(o=>
      (o.estado||'').toLowerCase().includes('complet')||
      (o.estado||'').toLowerCase().includes('entregad')
    ).length;
    // Si la OT tiene campo de papel/materiales, sumarlo
    ots.forEach(ot => {
      if (Number(ot.pesoMaterial||0)>0) {
        const mat = (ot.material||'').toLowerCase();
        if (mat.includes('papel')||mat.includes('cartul')||mat.includes('carton')) {
          resultado.papel_carton += Number(ot.pesoMaterial);
          resultado.fuentes.push({ origen:'OTs', campo:'papel_carton', valor:Number(ot.pesoMaterial), label:`OT ${ot.codigo||ot.id}: ${ot.pesoMaterial}kg` });
        }
      }
    });
  } catch(e) { console.warn('HC Auto: no se pudo leer ots', e); }

  return resultado;
}

// ─── SCORE AMBIENTAL ──────────────────────────────────────────
function calcularScore(total, produccionTon) {
  if (!produccionTon || produccionTon <= 0) return null;
  const intensidad = total / produccionTon;
  if (intensidad <= BENCHMARK_SECTOR * 0.8) return { letra:'A', color:'#22c55e', label:'Excelente — Por debajo del benchmark' };
  if (intensidad <= BENCHMARK_SECTOR)       return { letra:'B', color:'#3b82f6', label:'Bueno — En línea con el sector' };
  if (intensidad <= BENCHMARK_SECTOR * 1.2) return { letra:'C', color:'#f59e0b', label:'Regular — Levemente sobre el benchmark' };
  return { letra:'D', color:'#e63946', label:'Crítico — Más del 20% sobre el benchmark' };
}

// ─── PDF REPORTE ──────────────────────────────────────────────
function exportarReportePDF(datos, kpis, nivel_meta, historial, factores) {
  const { total, por_alcance } = calcularEmisiones(datos.consumos||{}, factores);
  const fecha = new Date().toLocaleDateString('es-AR');
  const variacion = datos.anioAnterior > 0 ? (((total - Number(datos.anioAnterior)) / Number(datos.anioAnterior)) * 100).toFixed(1) : null;
  const intensidad = Number(datos.produccion||0) > 0 ? (total / Number(datos.produccion)).toFixed(4) : null;

  const html = `<!DOCTYPE html>
<html lang="es"><head><meta charset="utf-8"><title>Huella de Carbono — ALQOS™</title>
<style>
* { margin:0; padding:0; box-sizing:border-box; }
body { font-family: Arial, sans-serif; font-size: 11px; color: #2B2F36; }
.header { background: #1C3FAA; color: white; padding: 28px 40px; }
.header h1 { font-size: 20px; font-weight: 800; margin-bottom: 4px; }
.header p { font-size: 11px; opacity: 0.8; }
.kpi-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 12px; padding: 20px 40px; background: #f4f6fb; }
.kpi { background: white; border-radius: 8px; padding: 12px; text-align: center; border: 1px solid #eee; }
.kpi-val { font-size: 20px; font-weight: 800; margin-bottom: 4px; }
.kpi-label { font-size: 10px; color: #888; }
.section { padding: 20px 40px; border-bottom: 1px solid #eee; }
.section h2 { font-size: 12px; font-weight: 700; color: #1C3FAA; text-transform: uppercase; letter-spacing: .1em; margin-bottom: 12px; }
.row { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 0.5px solid #eee; font-size: 11px; }
.bar-row { margin-bottom: 10px; }
.bar-label { display: flex; justify-content: space-between; font-size: 10px; margin-bottom: 3px; }
.bar-bg { background: #f0f0f0; border-radius: 4px; height: 8px; }
.bar-fill { height: 8px; border-radius: 4px; }
.footer { padding: 16px 40px; background: #1C3FAA; color: white; display: flex; justify-content: space-between; font-size: 10px; }
.tag { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: 700; }
.auto-badge { background:#e0f2fe; color:#0369a1; padding:2px 6px; border-radius:6px; font-size:9px; font-weight:700; margin-left:6px; }
</style></head>
<body>
<div class="header">
  <h1>🌱 ALQOS™ — Reporte Huella de Carbono</h1>
  <p>ISO 14064-1 · GHG Protocol · Generado: ${fecha} | Período: ${datos.periodo||"No especificado"}</p>
</div>

<div class="kpi-grid">
  <div class="kpi"><div class="kpi-val" style="color:#7B5BF2">${total.toFixed(3)}</div><div class="kpi-label">Total tCO₂eq</div></div>
  <div class="kpi"><div class="kpi-val" style="color:#e63946">${por_alcance.a1.toFixed(3)}</div><div class="kpi-label">Alcance 1 (tCO₂eq)</div></div>
  <div class="kpi"><div class="kpi-val" style="color:#f59e0b">${por_alcance.a2.toFixed(3)}</div><div class="kpi-label">Alcance 2 (tCO₂eq)</div></div>
  <div class="kpi"><div class="kpi-val" style="color:#3b82f6">${por_alcance.a3.toFixed(3)}</div><div class="kpi-label">Alcance 3 (tCO₂eq)</div></div>
  <div class="kpi"><div class="kpi-val" style="color:#22c55e">${intensidad||"—"}</div><div class="kpi-label">Intensidad tCO₂eq/unidad</div></div>
  <div class="kpi"><div class="kpi-val" style="color:${variacion&&Number(variacion)<0?"#22c55e":"#e63946"}">${variacion?variacion+"%":"—"}</div><div class="kpi-label">Variación vs año anterior</div></div>
</div>

<div class="section">
  <h2>Distribución por alcance</h2>
  ${Object.entries(por_alcance).map(([a,v])=>{
    const pct = total>0?(v/total*100).toFixed(1):0;
    const col = ALCANCE_COLOR[a];
    return `<div class="bar-row">
      <div class="bar-label"><span>${ALCANCE_LABEL[a]}</span><span style="color:${col};font-weight:700">${v.toFixed(3)} t · ${pct}%</span></div>
      <div class="bar-bg"><div class="bar-fill" style="width:${pct}%;background:${col}"></div></div>
    </div>`;
  }).join('')}
</div>

<div class="section">
  <h2>Detalle de emisiones por fuente</h2>
  ${Object.entries(factores||FACTORES_BASE).map(([alcance, fuentes])=>
    fuentes.filter(f=>Number((datos.consumos||{})[f.id]||0)>0).map(f=>{
      const val = Number((datos.consumos||{})[f.id]||0);
      const em = (val*f.factor/1000).toFixed(4);
      const esAuto = (datos.consumosAuto||{})[f.id];
      return `<div class="row">
        <span style="color:${ALCANCE_COLOR[alcance]};font-weight:600">[${alcance.toUpperCase()}]</span>
        <span style="flex:1;margin-left:8px">${f.label}${esAuto?'<span class="auto-badge">AUTO</span>':''} — ${val} ${f.unidad}</span>
        <span style="font-weight:700">${em} tCO₂eq</span>
      </div>`;
    }).join('')
  ).join('')}
</div>

${datos.meta?`<div class="section">
  <h2>Meta de reducción</h2>
  <div class="row"><span>Meta período</span><span style="font-weight:700;color:#22c55e">${datos.meta} tCO₂eq</span></div>
  <div class="row"><span>Estado</span><span style="font-weight:700;color:${total<=Number(datos.meta)?"#22c55e":"#e63946"}">${total<=Number(datos.meta)?"✅ Meta cumplida":"⚠️ Meta no alcanzada"}</span></div>
  <div class="row"><span>Desvío</span><span style="font-weight:700">${(total-Number(datos.meta)).toFixed(3)} tCO₂eq</span></div>
</div>`:""}

${datos.planAcciones?.length>0?`<div class="section">
  <h2>Plan de acción de reducción</h2>
  ${datos.planAcciones.map(a=>`<div class="row">
    <span style="flex:1">${a.accion}</span>
    <span style="margin:0 8px;color:#888">${a.responsable||"—"} · ${a.fechaCompromiso||"—"}</span>
    <span style="font-weight:700;color:#22c55e">${a.reduccionEstimada||0} t</span>
    <span class="tag" style="background:${a.estado==="completado"?"#f0fdf4":a.estado==="en_proceso"?"#eff6ff":"#fefce8"};color:${a.estado==="completado"?"#22c55e":a.estado==="en_proceso"?"#3b82f6":"#f59e0b"};margin-left:6px">${a.estado}</span>
  </div>`).join('')}
</div>`:""}

${historial.length>0?`<div class="section">
  <h2>Evolución histórica</h2>
  ${historial.slice(0,5).map(h=>`<div class="row">
    <span>${h.periodo||h.anio}</span>
    <span style="font-weight:700;color:#7B5BF2">${Number(h.total).toFixed(3)} tCO₂eq</span>
    ${h.variacion?`<span style="color:${Number(h.variacion)<0?"#22c55e":"#e63946"};font-weight:600">${h.variacion}%</span>`:""}
  </div>`).join('')}
</div>`:""}

<div class="footer">
  <span>ALQOS™ by QORVEX · Sistema de Gestión Integral · ISO 14064-1 · GHG Protocol</span>
  <span>Generado: ${fecha}</span>
</div>
</body></html>`;

  const w = window.open("","_blank");
  w.document.write(html);
  w.document.close();
  setTimeout(()=>w.print(), 600);
}

// ════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ════════════════════════════════════════════════════════════
export default function HuellaCarbono({ onVolver, oscuro, usuario }) {
  const [datos, setDatos]       = useState(() => storage.get(STORAGE_HC));
  const [historial, setHistorial] = useState(() => storage.get(STORAGE_HC_HIST, []));
  const [factores, setFactores] = useState(cargarFactores);
  const [datosAuto, setDatosAuto] = useState(null);
  const [mostrarAuto, setMostrarAuto] = useState(false);
  const [tab, setTab]           = useState('a1');
  const [cargandoARIA, setCargandoARIA] = useState(false);
  const [formComp, setFormComp] = useState({ descripcion:'', tCO2:'', fecha:'', tipo:'compensacion' });
  const [formPlan, setFormPlan] = useState({ accion:'', alcance:'a1', responsable:'', fechaCompromiso:'', reduccionEstimada:'', estado:'pendiente' });

  // Config factores

  const [editandoFactor, setEditandoFactor] = useState(null); // {alcance, id}
  const [formNuevoFactor, setFormNuevoFactor] = useState({ alcance:'a3', label:'', unidad:'kg', factor:'', fuente:'' });

  const esAdmin = usuario?.rol === "Administrador";

  // ─── ESTILOS ────────────────────────────────────────────────
  const bg     = oscuro ? "#09184D" : "#F0EEFF";
  const cardBg = oscuro ? "#0d2160" : "white";
  const texto  = oscuro ? "#EDF2FA" : "#2F3E2E";
  const borde  = oscuro ? "#1e3a8a" : "#eee";
  const navBg  = oscuro ? "#060f30" : "#09184D";
  const sub    = oscuro ? "#9aabd4" : "#808085";
  const card   = { background:cardBg, borderRadius:"12px", padding:"1.5rem", border:`1px solid ${borde}`, marginBottom:"12px" };
  const inp    = { width:"100%", padding:"8px 12px", borderRadius:"8px", border:`1px solid ${borde}`, fontSize:"13px", boxSizing:"border-box", marginTop:"4px", background:cardBg, color:texto, fontFamily:"sans-serif" };
  const lbl    = { fontSize:"11px", color:sub, fontWeight:"700", display:"block", marginTop:"12px", textTransform:"uppercase", letterSpacing:".05em" };

  // ─── SUPABASE SYNC ──────────────────────────────────────────
  useEffect(() => {
    async function cargarDeSupabase() {
      try {
        const tid = Number(localStorage.getItem("alqos_tenant_id")) || null;
        if (!tid) return;
        const { supabase } = await import('./supabase');
        const { data } = await supabase.from('huella_carbono').select('data').eq('tenant_id', tid).order('id', { ascending: true });
        if (data && data.length > 0) {
          data.forEach(row => {
            if (!row.data) return;
            if (row.data.historial) {
              setHistorial(row.data.historial);
              storage.set(STORAGE_HC_HIST, row.data.historial);
            } else {
              setDatos(row.data);
              storage.set(STORAGE_HC, row.data);
            }
          });
        }
      } catch(e) { console.error('Error cargando huella desde Supabase', e); }
    }
    cargarDeSupabase();
  }, []);

  // ─── LEER DATOS AUTOMÁTICOS AL MONTAR ───────────────────────
  useEffect(() => {
    const auto = leerDatosAuto();
    setDatosAuto(auto);
  }, []);

  // ─── APLICAR DATOS AUTOMÁTICOS ──────────────────────────────
  const aplicarDatosAuto = useCallback(() => {
    if (!datosAuto) return;
    const nuevosConsumos = { ...datos.consumos };
    const marcas = {};

    if (datosAuto.papel_carton > 0) {
      nuevosConsumos.papel_carton = (Number(nuevosConsumos.papel_carton||0) + datosAuto.papel_carton).toFixed(2);
      marcas.papel_carton = true;
    }
    if (datosAuto.plancha_offset > 0) {
      nuevosConsumos.plancha_offset = (Number(nuevosConsumos.plancha_offset||0) + datosAuto.plancha_offset).toFixed(2);
      marcas.plancha_offset = true;
    }
    if (datosAuto.tintas > 0) {
      nuevosConsumos.tintas = (Number(nuevosConsumos.tintas||0) + datosAuto.tintas).toFixed(2);
      marcas.tintas = true;
    }

    guardar({ ...datos, consumos: nuevosConsumos, consumosAuto: { ...(datos.consumosAuto||{}), ...marcas } });
    setMostrarAuto(false);
    alert(`✅ Datos automáticos aplicados:\n• Papel/cartón: +${datosAuto.papel_carton.toFixed(2)} kg\n• Planchas: +${datosAuto.plancha_offset.toFixed(2)} kg\n• Tintas: +${datosAuto.tintas.toFixed(2)} kg\n\nRevisá los valores en Alcance 3 antes de cerrar el período.`);
  }, [datosAuto, datos]);

  const consumos     = useMemo(() => datos.consumos || {}, [datos.consumos]);
  const planAcciones = datos.planAcciones || [];
  const compensaciones = datos.compensaciones || [];

  const { total, por_alcance } = useMemo(() => calcularEmisiones(consumos, factores), [consumos, factores]);
  const totalCompensado = compensaciones.reduce((a,c)=>a+(Number(c.tCO2)||0),0);
  const totalNeto       = Math.max(0, total - totalCompensado);
  const produccionNum   = Number(datos.produccion||0);
  const intensidad      = produccionNum > 0 ? (total / produccionNum).toFixed(4) : null;
  const anioAnt         = Number(datos.anioAnterior||0);
  const variacion       = anioAnt > 0 ? (((total - anioAnt) / anioAnt) * 100).toFixed(1) : null;
  const meta            = Number(datos.meta||0);
  const pctMeta         = meta > 0 ? Math.min(100, (total/meta*100)).toFixed(0) : null;

  // Score ambiental (producción en toneladas)
  const produccionTon = produccionNum > 0 ? produccionNum / 1000 : 0;
  const score = calcularScore(total, produccionTon);

  // Alertas inteligentes
  const alertas = useMemo(() => {
    const lista = [];
    if (variacion && Number(variacion) > 10)
      lista.push({ tipo:'danger', msg:`⚠️ Desvío +${variacion}% vs año anterior — superó el umbral del 10%`, accion:'Revisá el plan de reducción' });
    if (meta > 0 && total > meta)
      lista.push({ tipo:'danger', msg:`🎯 Meta no alcanzada: ${(total-meta).toFixed(3)} tCO₂eq sobre el límite`, accion:'Evaluá compensaciones o acelerá acciones del plan' });
    if (total > 0) {
      const fuentePrincipal = Object.entries(FACTORES_BASE).flatMap(([alc,fs])=>
        fs.map(f=>({label:f.label,em:Number(consumos[f.id]||0)*f.factor/1000}))
      ).sort((a,b)=>b.em-a.em)[0];
      if (fuentePrincipal && fuentePrincipal.em/total > 0.5)
        lista.push({ tipo:'warning', msg:`📌 "${fuentePrincipal.label}" representa más del 50% de tus emisiones`, accion:'Priorizá acciones sobre esta fuente' });
    }
    if (datosAuto && (datosAuto.papel_carton>0 || datosAuto.plancha_offset>0) && !datos.consumosAuto)
      lista.push({ tipo:'info', msg:`🔄 Hay datos disponibles de otros módulos para importar automáticamente`, accion:'Usá el botón "Importar datos auto"' });
    if (score?.letra === 'D')
      lista.push({ tipo:'danger', msg:`📊 Score D: intensidad muy por encima del benchmark sectorial (${BENCHMARK_SECTOR} tCO₂eq/t)`, accion:'Revisá eficiencia de procesos' });
    return lista;
  }, [variacion, meta, total, consumos, datosAuto, datos.consumosAuto, score]);

  // ─── GUARDAR ────────────────────────────────────────────────
  const guardar = (nuevos) => {
    setDatos(nuevos);
    storage.set(STORAGE_HC, nuevos);
    import('./supabase').then(({supabase})=>{
      const tid = Number(localStorage.getItem("alqos_tenant_id")) || null;
      supabase.from('huella_carbono').upsert({ id: tid ? tid*2-1 : null, data: nuevos, tenant_id: tid }).catch(()=>{});
    }).catch(()=>{});
  };

  const guardarHistorial = (nuevo_hist) => {
    setHistorial(nuevo_hist);
    storage.set(STORAGE_HC_HIST, nuevo_hist);
    import('./supabase').then(({supabase})=>{
      const tid = Number(localStorage.getItem("alqos_tenant_id")) || null;
      supabase.from('huella_carbono').upsert({ id: tid ? tid*2 : null, data: { historial: nuevo_hist }, tenant_id: tid }).catch(()=>{});
    }).catch(()=>{});
  };

  const setConsumo = (id, val) => guardar({ ...datos, consumos: { ...consumos, [id]: val } });

  const cerrarPeriodo = () => {
    if (!window.confirm(`¿Cerrar el período "${datos.periodo||"actual"}" y guardar en historial?`)) return;
    const entrada = {
      id: Date.now(),
      periodo: datos.periodo||new Date().getFullYear().toString(),
      anio: new Date().getFullYear(),
      total: total.toFixed(3),
      a1: por_alcance.a1.toFixed(3),
      a2: por_alcance.a2.toFixed(3),
      a3: por_alcance.a3.toFixed(3),
      variacion,
      produccion: datos.produccion,
      intensidad,
      score: score?.letra,
      fechaCierre: new Date().toISOString(),
      cerradoPor: usuario?.nombre,
    };
    const nuevo_hist = [entrada, ...historial].slice(0,10);
    guardarHistorial(nuevo_hist);
    guardar({ consumos:{}, periodo:'', produccion:'', anioAnterior: total.toFixed(3), meta:'', planAcciones:[], compensaciones:[], consumosAuto:{} });
    setTab('a1');
    alert('✅ Período cerrado y guardado en historial.');
  };

  // ─── FACTORES: guardar cambios ───────────────────────────────
  const guardarFactorEditado = (alcance, id, nuevoFactor, nuevaFuente) => {
    const nuevosFactores = { ...factores };
    nuevosFactores[alcance] = factores[alcance].map(f =>
      f.id === id ? { ...f, factor: Number(nuevoFactor), fuente: nuevaFuente } : f
    );
    setFactores(nuevosFactores);
    // Guardar solo los modificados vs base
    const custom = {};
    Object.entries(nuevosFactores).forEach(([alc, lista]) => {
      const modificados = lista.filter(f => {
        const base = (FACTORES_BASE[alc]||[]).find(b=>b.id===f.id);
        return !base || base.factor!==f.factor || base.fuente!==f.fuente || !FACTORES_BASE[alc];
      });
      if (modificados.length > 0) custom[alc] = modificados;
    });
    localStorage.setItem(STORAGE_FACTORES_CUSTOM, JSON.stringify(custom));
    import('./supabase').then(({supabase}) => { const tid = Number(localStorage.getItem("alqos_tenant_id")) || null; supabase.from('hc_factores_custom').upsert({ id: tid, data: custom, tenant_id: tid }).catch(()=>{}); });
    setEditandoFactor(null);
  };

  const agregarFactor = () => {
    if (!formNuevoFactor.label.trim() || !formNuevoFactor.factor) return;
    const nuevo = { ...formNuevoFactor, id:`custom_${Date.now()}`, factor:Number(formNuevoFactor.factor) };
    const nuevosFactores = { ...factores, [nuevo.alcance]: [...(factores[nuevo.alcance]||[]), nuevo] };
    setFactores(nuevosFactores);
    const custom = JSON.parse(localStorage.getItem(STORAGE_FACTORES_CUSTOM)||'{}');
    if (!custom[nuevo.alcance]) custom[nuevo.alcance] = [];
    custom[nuevo.alcance].push(nuevo);
    localStorage.setItem(STORAGE_FACTORES_CUSTOM, JSON.stringify(custom));
    import('./supabase').then(({supabase}) => { const tid = Number(localStorage.getItem("alqos_tenant_id")) || null; supabase.from('hc_factores_custom').upsert({ id: tid, data: custom, tenant_id: tid }).catch(()=>{}); });
    setFormNuevoFactor({ alcance:'a3', label:'', unidad:'kg', factor:'', fuente:'' });
  };

  const eliminarFactorCustom = (alcance, id) => {
    if (FACTORES_BASE[alcance]?.find(f=>f.id===id)) { alert('No podés eliminar factores base. Solo podés editar su valor.'); return; }
    const nuevosFactores = { ...factores, [alcance]: factores[alcance].filter(f=>f.id!==id) };
    setFactores(nuevosFactores);
    const custom = JSON.parse(localStorage.getItem(STORAGE_FACTORES_CUSTOM)||'{}');
    if (custom[alcance]) custom[alcance] = custom[alcance].filter(f=>f.id!==id);
    localStorage.setItem(STORAGE_FACTORES_CUSTOM, JSON.stringify(custom));
    import('./supabase').then(({supabase}) => { const tid = Number(localStorage.getItem("alqos_tenant_id")) || null; supabase.from('hc_factores_custom').upsert({ id: tid, data: custom, tenant_id: tid }).catch(()=>{}); });
  };

  const resetFactores = () => {
    if (!window.confirm('¿Resetear todos los factores a los valores base?')) return;
    localStorage.removeItem(STORAGE_FACTORES_CUSTOM);
    setFactores(FACTORES_BASE);
    import('./supabase').then(({supabase}) => { const tid = Number(localStorage.getItem("alqos_tenant_id")) || null; supabase.from('hc_factores_custom').upsert({ id: tid, data: {}, tenant_id: tid }).catch(()=>{}); });
  };

  // ─── ARIA ───────────────────────────────────────────────────
  const generarPlanARIA = async () => {
    setCargandoARIA(true);
    try {
      const resumen = Object.entries(factores).flatMap(([alcance, fuentes]) =>
        fuentes.filter(f=>Number(consumos[f.id]||0)>0).map(f=>`${f.label}: ${consumos[f.id]} ${f.unidad} → ${(Number(consumos[f.id])*f.factor/1000).toFixed(3)} tCO₂eq`)
      ).join('\n');

      const autoInfo = datosAuto ? `
Datos automáticos integrados desde otros módulos:
- OTs completadas en el período: ${datosAuto.ots_completadas}
- Papel/cartón desde Ingreso Materiales: ${datosAuto.papel_carton.toFixed(2)} kg
- Planchas offset desde Pre Prensa: ${datosAuto.plancha_offset.toFixed(2)} kg
` : '';

      const prompt = `Sos ARIA, experto en gestión ambiental ISO 14001 y huella de carbono GHG Protocol para industria gráfica de packaging. 
Analizá los datos de una pyme gráfica argentina de packaging secundario y generá 5 acciones de reducción concretas, prácticas y adaptadas a su realidad (pyme, Argentina, industria gráfica).
Sin markdown. SOLO JSON array. Sin explicaciones previas ni posteriores.

Emisiones por fuente:
${resumen||"Sin datos cargados aún"}

Total: ${total.toFixed(3)} tCO₂eq | Período: ${datos.periodo||"No especificado"}
${intensidad?`Intensidad: ${intensidad} tCO₂eq/unidad`:""}
${anioAnt?`Variación vs año anterior: ${variacion}%`:""}
${score?`Score ambiental: ${score.letra} — ${score.label}`:""}
Benchmark sector gráfico AR: ${BENCHMARK_SECTOR} tCO₂eq/t papel procesado
${autoInfo}

Reglas para las acciones:
- Priorizá las fuentes con mayor peso en el total
- Incluí al menos 1 acción de Alcance 2 si hay electricidad de red
- Incluí al menos 1 acción vinculada a residuos o eficiencia de materiales
- Las fechas deben estar entre 2026 y 2027
- La reducción estimada debe ser realista para una pyme

JSON exacto (array de 5 objetos, sin ningún texto antes ni después):
[{"accion":"descripción concreta de la acción","alcance":"a1","responsable":"rol del responsable","fechaCompromiso":"YYYY-MM-DD","reduccionEstimada":0.5,"estado":"pendiente"}]`;

      const response = await fetch("/api/aria", {
        method:"POST",
        headers:{ ...(await authHeader()),
          "Content-Type":"application/json",
          "anthropic-version":"2023-06-01",
          },
        body: JSON.stringify({ model:"claude-sonnet-4-5", max_tokens:1500, messages:[{role:"user",content:prompt}] })
      });
      const data = await response.json();
      const raw = data.content?.[0]?.text||"";
      const limpio = raw.replace(/```json/g,"").replace(/```/g,"").trim();
      const acciones = JSON.parse(limpio);
      const nuevas = [...planAcciones, ...acciones.map(a=>({...a,id:Date.now()+Math.random(),origenARIA:true}))];
      guardar({...datos, planAcciones:nuevas});
      setTab('plan');
    } catch(e) {
      console.error(e);
      alert("Error al conectar con ARIA. Verificá la API key.");
    }
    setCargandoARIA(false);
  };

  const agregarAccion = () => {
    if (!formPlan.accion.trim()) return;
    const nuevas = [...planAcciones, {...formPlan, id:Date.now()}];
    guardar({...datos, planAcciones:nuevas});
    setFormPlan({accion:'',alcance:'a1',responsable:'',fechaCompromiso:'',reduccionEstimada:'',estado:'pendiente'});
  };

  const agregarCompensacion = () => {
    if (!formComp.descripcion.trim()||!formComp.tCO2) return;
    const nuevas = [...compensaciones, {...formComp, id:Date.now()}];
    guardar({...datos, compensaciones:nuevas});
    setFormComp({descripcion:'',tCO2:'',fecha:'',tipo:'compensacion'});
  };

  const exportarCSV = () => {
    const rows = [
      ["HUELLA DE CARBONO — ALQOS™"],["Período:", datos.periodo||"—"],
      ["Total bruto (tCO₂eq):", total.toFixed(3)],
      ["Compensaciones (tCO₂eq):", totalCompensado.toFixed(3)],
      ["Total neto (tCO₂eq):", totalNeto.toFixed(3)],
      ["Score ambiental:", score?.letra||"—"],[""],
      ["ALCANCE","FUENTE","UNIDAD","CONSUMO","FACTOR kgCO₂eq/u","EMISIONES tCO₂eq","ORIGEN"],
    ];
    Object.entries(factores).forEach(([alcance, fuentes]) => {
      fuentes.forEach(f => {
        const val = Number(consumos[f.id]||0);
        if(val>0) rows.push([
          ALCANCE_LABEL[alcance], f.label, f.unidad, val, f.factor,
          (val*f.factor/1000).toFixed(4),
          (datos.consumosAuto||{})[f.id]?"Automático":"Manual"
        ]);
      });
    });
    if(compensaciones.length>0){
      rows.push([""],["COMPENSACIONES"],["Descripción","Tipo","tCO₂eq","Fecha"]);
      compensaciones.forEach(c=>rows.push([c.descripcion,c.tipo,c.tCO2,c.fecha]));
    }
    const csv = rows.map(r=>r.map(c=>`"${c}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF"+csv],{type:"text/csv;charset=utf-8;"});
    const a = document.createElement("a"); a.href=URL.createObjectURL(blob);
    a.download=`HuellaCarbono_${new Date().toISOString().slice(0,10)}.csv`; a.click();
  };

  const tabs = [
    {id:'a1',label:'🔴 Alcance 1',color:'#e63946'},
    {id:'a2',label:'🟡 Alcance 2',color:'#f59e0b'},
    {id:'a3',label:'🔵 Alcance 3',color:'#3b82f6'},
    {id:'compensaciones',label:'🌿 Compensaciones',color:'#22c55e'},
    {id:'resumen',label:'📊 Resumen',color:'#7B5BF2'},
    {id:'plan',label:'📋 Plan',color:'#22c55e'},
    {id:'historial',label:'📅 Historial',color:'#6b7280'},
    ...(esAdmin?[{id:'factores',label:'⚙️ Factores',color:'#6b7280'}]:[]),
  ];

  const estadoPlanColor = {pendiente:'#f59e0b',en_proceso:'#3b82f6',completado:'#22c55e',cancelado:'#e63946'};

  // ════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════
  return (
    <div style={{minHeight:"100vh",background:bg,fontFamily:"sans-serif"}}>

      {/* NAVBAR */}
      <div style={{background:navBg,padding:"1rem 1.5rem",display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:100,boxShadow:"0 2px 12px rgba(0,0,0,.3)"}}>
        <div style={{display:"flex",alignItems:"center",gap:"12px"}}>
          <button onClick={onVolver} style={{background:"rgba(255,255,255,0.1)",border:"none",color:"white",padding:"6px 14px",borderRadius:"10px",cursor:"pointer",fontSize:"13px"}}>← Menú</button>
          <BotonInstructivo moduloKey="huella_carbono" oscuro={oscuro} />
          {usuario?.rol === "Administrador" && <BotonMapaProceso moduloKey="huella_carbono" oscuro={oscuro} />}
          <span style={{color:"white",fontWeight:"700",fontSize:"16px"}}>🌱 Huella de Carbono</span>
          {score && (
            <span style={{padding:"3px 10px",borderRadius:"8px",fontSize:"12px",fontWeight:"800",background:score.color,color:"white"}}>
              Score {score.letra}
            </span>
          )}
        </div>
        <div style={{display:"flex",gap:"6px",flexWrap:"wrap"}}>
          {datosAuto && (datosAuto.papel_carton>0||datosAuto.plancha_offset>0) && (
            <button onClick={()=>setMostrarAuto(m=>!m)}
              style={{padding:"6px 10px",background:"rgba(59,130,246,.3)",border:"1px solid rgba(59,130,246,.5)",borderRadius:"8px",cursor:"pointer",fontSize:"11px",color:"white",fontWeight:"600"}}>
              🔄 Auto
            </button>
          )}
          <button onClick={generarPlanARIA} disabled={cargandoARIA}
            style={{padding:"6px 10px",background:cargandoARIA?"rgba(123,91,242,.3)":"linear-gradient(135deg,#7B5BF2,#09184D)",border:"none",borderRadius:"8px",cursor:cargandoARIA?"default":"pointer",fontSize:"11px",color:"white",fontWeight:"600"}}>
            {cargandoARIA?"🤖 Generando...":"🤖 ARIA"}
          </button>
          <button onClick={()=>exportarReportePDF(datos,{total,por_alcance},datos.meta,historial,factores)}
            style={{padding:"6px 10px",background:"rgba(255,255,255,0.1)",border:"none",borderRadius:"8px",cursor:"pointer",fontSize:"11px",color:"white"}}>📄 PDF</button>
          <button onClick={exportarCSV}
            style={{padding:"6px 10px",background:"rgba(255,255,255,0.1)",border:"none",borderRadius:"8px",cursor:"pointer",fontSize:"11px",color:"white"}}>📊 CSV</button>
          <button onClick={cerrarPeriodo}
            style={{padding:"6px 10px",background:"rgba(34,197,94,.2)",border:"1px solid rgba(34,197,94,.4)",borderRadius:"8px",cursor:"pointer",fontSize:"11px",color:"#22c55e",fontWeight:"600"}}>✅ Cerrar período</button>
          <button onClick={()=>{ if(window.confirm('¿Borrar todos los datos del período actual?')){ guardar({consumos:{},periodo:'',produccion:'',anioAnterior:'',meta:'',planAcciones:[],compensaciones:[],consumosAuto:{}}); setTab('a1'); }}}
            style={{padding:"6px 10px",background:"rgba(230,57,70,.2)",border:"1px solid rgba(230,57,70,.4)",borderRadius:"8px",cursor:"pointer",fontSize:"11px",color:"#e63946",fontWeight:"600"}}>🗑 Borrar</button>
        </div>
      </div>

      <div style={{padding:"1rem 1.5rem",maxWidth:"900px",margin:"0 auto"}}>

        {/* PANEL DATOS AUTOMÁTICOS */}
        {mostrarAuto && datosAuto && (
          <div style={{background:oscuro?"#0a1830":"#eff6ff",border:"2px solid #3b82f6",borderRadius:"14px",padding:"1.2rem",marginBottom:"1rem"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"12px"}}>
              <h3 style={{fontSize:"14px",fontWeight:"700",color:"#3b82f6",margin:0}}>🔄 Datos disponibles desde otros módulos</h3>
              <button onClick={()=>setMostrarAuto(false)} style={{background:"none",border:"none",color:sub,cursor:"pointer",fontSize:"18px"}}>×</button>
            </div>
            <p style={{fontSize:"12px",color:sub,marginBottom:"12px"}}>Estos datos fueron calculados automáticamente desde Ingreso de Materiales, Pre Prensa y OTs. Podés importarlos con un clic.</p>
            {[
              {label:"Papel / cartón consumido",val:datosAuto.papel_carton,unidad:"kg",campo:"papel_carton",alcance:"A3"},
              {label:"Planchas offset aluminio",val:datosAuto.plancha_offset,unidad:"kg",campo:"plancha_offset",alcance:"A3"},
              {label:"Tintas y barnices",val:datosAuto.tintas,unidad:"kg",campo:"tintas",alcance:"A3"},
            ].filter(i=>i.val>0).map(item=>(
              <div key={item.campo} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 12px",borderRadius:"8px",background:oscuro?"#091840":"white",border:`1px solid ${borde}`,marginBottom:"6px"}}>
                <div>
                  <span style={{fontSize:"13px",fontWeight:"600",color:texto}}>{item.label}</span>
                  <span style={{fontSize:"11px",color:sub,marginLeft:"8px"}}>[{item.alcance}]</span>
                </div>
                <span style={{fontSize:"14px",fontWeight:"700",color:"#3b82f6"}}>{Number(item.val).toFixed(2)} {item.unidad}</span>
              </div>
            ))}
            {datosAuto.ots_completadas > 0 && (
              <p style={{fontSize:"11px",color:sub,marginTop:"8px"}}>📋 OTs completadas en el período: <strong style={{color:texto}}>{datosAuto.ots_completadas}</strong></p>
            )}
            {datosAuto.fuentes.length > 0 && (
              <details style={{marginTop:"8px"}}>
                <summary style={{fontSize:"11px",color:sub,cursor:"pointer"}}>Ver detalle de fuentes ({datosAuto.fuentes.length})</summary>
                <div style={{marginTop:"6px",padding:"8px",background:oscuro?"#060f30":"#f8fafc",borderRadius:"6px"}}>
                  {datosAuto.fuentes.map((f,i)=>(
                    <p key={i} style={{fontSize:"10px",color:sub,margin:"2px 0"}}>• [{f.origen}] {f.label}</p>
                  ))}
                </div>
              </details>
            )}
            <div style={{display:"flex",gap:"8px",marginTop:"12px"}}>
              <button onClick={aplicarDatosAuto}
                style={{flex:1,padding:"10px",background:"#3b82f6",color:"white",border:"none",borderRadius:"10px",cursor:"pointer",fontWeight:"700",fontSize:"13px"}}>
                ✅ Importar datos automáticos
              </button>
              <button onClick={()=>setMostrarAuto(false)}
                style={{padding:"10px 16px",background:"none",border:`1px solid ${borde}`,borderRadius:"10px",cursor:"pointer",color:texto,fontSize:"13px"}}>
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* ALERTAS */}
        {alertas.map((al,i)=>(
          <div key={i} style={{
            background: al.tipo==='danger'?(oscuro?"#1a0505":"#fff5f5"):al.tipo==='warning'?(oscuro?"#1a1000":"#fffbeb"):(oscuro?"#0a1830":"#eff6ff"),
            border:`1.5px solid ${al.tipo==='danger'?"#e63946":al.tipo==='warning'?"#f59e0b":"#3b82f6"}`,
            borderRadius:"10px",padding:"10px 14px",marginBottom:"8px",display:"flex",justifyContent:"space-between",alignItems:"center"
          }}>
            <div>
              <p style={{fontSize:"12px",fontWeight:"700",color:al.tipo==='danger'?"#e63946":al.tipo==='warning'?"#f59e0b":"#3b82f6",margin:0}}>{al.msg}</p>
              <p style={{fontSize:"11px",color:sub,margin:"2px 0 0"}}>{al.accion}</p>
            </div>
          </div>
        ))}

        {/* SCORE AMBIENTAL */}
        {score && (
          <div style={{...card,background:oscuro?"#0a1f5c":"#eff6ff",border:`2px solid ${score.color}40`,display:"flex",alignItems:"center",gap:"1.5rem",padding:"1rem 1.5rem"}}>
            <div style={{textAlign:"center",minWidth:"64px"}}>
              <p style={{fontSize:"40px",fontWeight:"900",color:score.color,margin:0,lineHeight:1}}>{score.letra}</p>
              <p style={{fontSize:"10px",color:sub,margin:"4px 0 0",fontWeight:"700"}}>SCORE</p>
            </div>
            <div style={{flex:1}}>
              <p style={{fontSize:"13px",fontWeight:"700",color:texto,margin:"0 0 2px"}}>{score.label}</p>
              <p style={{fontSize:"11px",color:sub,margin:0}}>
                Benchmark sector gráfico AR: {BENCHMARK_SECTOR} tCO₂eq/t · Tu intensidad: {(total/produccionTon||0).toFixed(3)} tCO₂eq/t
              </p>
            </div>
            <div style={{textAlign:"right"}}>
              <p style={{fontSize:"10px",color:sub,margin:"0 0 2px"}}>OTs completadas</p>
              <p style={{fontSize:"18px",fontWeight:"800",color:texto,margin:0}}>{datosAuto?.ots_completadas||"—"}</p>
            </div>
          </div>
        )}

        {/* KPIs */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(150px,1fr))",gap:"10px",marginBottom:"1rem"}}>
          {[
            {label:"Total bruto",val:`${total.toFixed(3)} t`,color:"#7B5BF2"},
            {label:"Compensaciones",val:`-${totalCompensado.toFixed(3)} t`,color:"#22c55e"},
            {label:"Total neto",val:`${totalNeto.toFixed(3)} t`,color:"#1C3FAA"},
            {label:"Alcance 1",val:`${por_alcance.a1.toFixed(3)} t`,color:"#e63946"},
            {label:"Alcance 2",val:`${por_alcance.a2.toFixed(3)} t`,color:"#f59e0b"},
            {label:"Alcance 3",val:`${por_alcance.a3.toFixed(3)} t`,color:"#3b82f6"},
            {label:"Intensidad",val:intensidad?`${intensidad} t/u`:"—",color:"#22c55e"},
            {label:"Variación",val:variacion?`${variacion}%`:"—",color:variacion&&Number(variacion)<0?"#22c55e":"#e63946"},
          ].map(({label,val,color})=>(
            <div key={label} style={{background:cardBg,borderRadius:"12px",padding:"1rem",border:`1px solid ${borde}`,textAlign:"center"}}>
              <p style={{fontSize:"16px",fontWeight:"800",color,margin:"0 0 4px"}}>{val}</p>
              <p style={{fontSize:"10px",color:sub,margin:0}}>{label}</p>
            </div>
          ))}
        </div>

        {/* META */}
        <div style={{...card,background:oscuro?"#0a1f5c":"#eff6ff",border:"1px solid #3b82f640"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:"10px",alignItems:"end"}}>
            <div>
              <span style={lbl}>Período</span>
              <input value={datos.periodo||""} onChange={e=>guardar({...datos,periodo:e.target.value})} style={inp} placeholder="Ej: 2026"/>
            </div>
            <div>
              <span style={lbl}>Producción anual (u)</span>
              <input type="number" value={datos.produccion||""} onChange={e=>guardar({...datos,produccion:e.target.value})} style={inp} placeholder="0"/>
            </div>
            <div>
              <span style={lbl}>Meta (tCO₂eq)</span>
              <input type="number" value={datos.meta||""} onChange={e=>guardar({...datos,meta:e.target.value})} style={inp} placeholder="0"/>
            </div>
            <div>
              <span style={lbl}>Año anterior (tCO₂eq)</span>
              <input type="number" value={datos.anioAnterior||""} onChange={e=>guardar({...datos,anioAnterior:e.target.value})} style={inp} placeholder="0"/>
            </div>
          </div>
          {meta>0&&(
            <div style={{marginTop:"12px"}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:"4px"}}>
                <span style={{fontSize:"11px",color:sub}}>Progreso vs meta</span>
                <span style={{fontSize:"11px",fontWeight:"700",color:total<=meta?"#22c55e":"#e63946"}}>{total.toFixed(3)} / {meta} tCO₂eq · {pctMeta}%</span>
              </div>
              <div style={{background:oscuro?"#091840":"#f4f6fb",borderRadius:"6px",height:"10px",overflow:"hidden"}}>
                <div style={{width:`${Math.min(100,pctMeta)}%`,height:"100%",background:total<=meta?"#22c55e":"#e63946",borderRadius:"6px",transition:"width .3s"}}/>
              </div>
            </div>
          )}
        </div>

        {/* TABS */}
        <div style={{display:"flex",gap:"4px",marginBottom:"1rem",overflowX:"auto",paddingBottom:"4px"}}>
          {tabs.map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)}
              style={{padding:"7px 12px",borderRadius:"8px",border:"none",cursor:"pointer",fontSize:"11px",fontWeight:"700",whiteSpace:"nowrap",background:tab===t.id?`linear-gradient(135deg,${t.color},${t.color}cc)`:"transparent",color:tab===t.id?"white":sub,flexShrink:0}}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── ALCANCES ── */}
        {['a1','a2','a3'].includes(tab)&&(
          <div style={card}>
            <h3 style={{fontSize:"14px",fontWeight:"700",color:ALCANCE_COLOR[tab],marginBottom:"4px"}}>{ALCANCE_LABEL[tab]}</h3>
            <p style={{fontSize:"11px",color:sub,marginBottom:"16px"}}>
              {tab==='a1'&&"Combustión de fuentes propias — gas, gasoil, nafta"}
              {tab==='a2'&&"Electricidad y energía comprada — CAMMESA 0.321 kgCO₂eq/kWh"}
              {tab==='a3'&&"Cadena de valor — agua, transporte, materiales, residuos, planchas"}
            </p>
            {factores[tab].map(f=>{
              const esAuto = (datos.consumosAuto||{})[f.id];
              return (
                <div key={f.id} style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",gap:"10px",marginBottom:"10px",alignItems:"center",padding:"8px",borderRadius:"8px",background:Number(consumos[f.id]||0)>0?(oscuro?"#0a1f0a":"#f0fdf4"):"transparent",border:Number(consumos[f.id]||0)>0?"1px solid #22c55e20":"none"}}>
                  <div>
                    <div style={{display:"flex",alignItems:"center",gap:"6px"}}>
                      <p style={{fontSize:"12px",fontWeight:"600",color:texto,margin:"0 0 2px"}}>{f.label}</p>
                      {esAuto && <span style={{fontSize:"9px",fontWeight:"700",background:"#dbeafe",color:"#1d4ed8",padding:"1px 5px",borderRadius:"4px"}}>AUTO</span>}
                    </div>
                    <p style={{fontSize:"10px",color:sub,margin:0}}>Factor: {f.factor} kgCO₂eq/{f.unidad} · {f.fuente}</p>
                  </div>
                  <input type="number" value={consumos[f.id]||""} onChange={e=>setConsumo(f.id,e.target.value)}
                    style={{...inp,marginTop:0,borderColor:"#f59e0b",background:oscuro?"#1a1000":"#fefce8"}} placeholder={f.unidad}/>
                  <div style={{textAlign:"center"}}>
                    <p style={{fontSize:"10px",color:sub,margin:"0 0 2px"}}>kgCO₂eq</p>
                    <p style={{fontSize:"13px",fontWeight:"700",color:ALCANCE_COLOR[tab],margin:0}}>{(Number(consumos[f.id]||0)*f.factor).toFixed(2)}</p>
                  </div>
                  <div style={{textAlign:"center"}}>
                    <p style={{fontSize:"10px",color:sub,margin:"0 0 2px"}}>tCO₂eq</p>
                    <p style={{fontSize:"13px",fontWeight:"700",color:ALCANCE_COLOR[tab],margin:0}}>{(Number(consumos[f.id]||0)*f.factor/1000).toFixed(4)}</p>
                  </div>
                </div>
              );
            })}
            <div style={{display:"flex",justifyContent:"flex-end",marginTop:"8px",padding:"10px",background:oscuro?"#091840":"#f4f6fb",borderRadius:"8px"}}>
              <span style={{fontSize:"13px",fontWeight:"700",color:ALCANCE_COLOR[tab]}}>Subtotal {tab.toUpperCase()}: {por_alcance[tab].toFixed(4)} tCO₂eq</span>
            </div>
          </div>
        )}

        {/* ── COMPENSACIONES ── */}
        {tab==='compensaciones'&&(
          <div>
            <div style={{...card,background:oscuro?"#0a1f0a":"#f0fdf4",border:"1px solid #22c55e40"}}>
              <h3 style={{fontSize:"13px",fontWeight:"700",color:"#22c55e",marginBottom:"10px"}}>+ Nueva compensación</h3>
              <input value={formComp.descripcion} onChange={e=>setFormComp(f=>({...f,descripcion:e.target.value}))} style={inp} placeholder="Descripción (ej: Créditos carbono certificados VCS)"/>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"8px",marginTop:"8px"}}>
                <div>
                  <span style={lbl}>Tipo</span>
                  <select value={formComp.tipo} onChange={e=>setFormComp(f=>({...f,tipo:e.target.value}))} style={inp}>
                    <option value="compensacion">Crédito de carbono</option>
                    <option value="forestacion">Forestación propia</option>
                    <option value="renovable">Energía renovable certificada</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
                <div>
                  <span style={lbl}>tCO₂eq compensadas</span>
                  <input type="number" value={formComp.tCO2} onChange={e=>setFormComp(f=>({...f,tCO2:e.target.value}))} style={inp} placeholder="0"/>
                </div>
                <div>
                  <span style={lbl}>Fecha</span>
                  <input type="date" value={formComp.fecha} onChange={e=>setFormComp(f=>({...f,fecha:e.target.value}))} style={inp}/>
                </div>
              </div>
              <button onClick={agregarCompensacion} style={{marginTop:"10px",padding:"8px 20px",background:"#22c55e",color:"white",border:"none",borderRadius:"8px",cursor:"pointer",fontSize:"13px",fontWeight:"700"}}>+ Agregar</button>
            </div>
            {compensaciones.length===0?(
              <div style={{textAlign:"center",padding:"2rem"}}>
                <p style={{fontSize:"32px"}}>🌿</p>
                <p style={{fontSize:"13px",color:sub}}>Sin compensaciones registradas</p>
              </div>
            ):compensaciones.map(c=>(
              <div key={c.id} style={{...card,borderLeft:"4px solid #22c55e"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <p style={{fontSize:"13px",fontWeight:"600",color:texto,margin:"0 0 2px"}}>{c.descripcion}</p>
                    <p style={{fontSize:"11px",color:sub,margin:0}}>{c.tipo} · {c.fecha||"—"}</p>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
                    <span style={{fontSize:"16px",fontWeight:"800",color:"#22c55e"}}>-{c.tCO2} tCO₂eq</span>
                    <button onClick={()=>guardar({...datos,compensaciones:compensaciones.filter(x=>x.id!==c.id)})} style={{background:"none",border:"none",color:"#e63946",cursor:"pointer",fontSize:"16px"}}>×</button>
                  </div>
                </div>
              </div>
            ))}
            <div style={{...card,background:oscuro?"#0a1f0a":"#f0fdf4",border:"1px solid #22c55e40",textAlign:"center"}}>
              <p style={{fontSize:"12px",color:sub,margin:"0 0 4px"}}>Total compensado</p>
              <p style={{fontSize:"24px",fontWeight:"800",color:"#22c55e",margin:"0 0 4px"}}>-{totalCompensado.toFixed(3)} tCO₂eq</p>
              <p style={{fontSize:"12px",color:sub}}>Huella neta: <strong style={{color:"#1C3FAA"}}>{totalNeto.toFixed(3)} tCO₂eq</strong></p>
            </div>
          </div>
        )}

        {/* ── RESUMEN ── */}
        {tab==='resumen'&&(
          <div>
            <div style={card}>
              <h3 style={{fontSize:"14px",fontWeight:"700",color:"#7B5BF2",marginBottom:"14px"}}>Distribución por alcance</h3>
              {Object.entries(por_alcance).map(([alcance,val])=>{
                const pct = total>0?(val/total*100).toFixed(1):0;
                return (
                  <div key={alcance} style={{marginBottom:"12px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:"4px"}}>
                      <span style={{fontSize:"12px",fontWeight:"600",color:texto}}>{ALCANCE_LABEL[alcance]}</span>
                      <span style={{fontSize:"12px",fontWeight:"700",color:ALCANCE_COLOR[alcance]}}>{val.toFixed(3)} t · {pct}%</span>
                    </div>
                    <div style={{background:oscuro?"#091840":"#f4f6fb",borderRadius:"6px",height:"10px",overflow:"hidden"}}>
                      <div style={{width:`${pct}%`,height:"100%",background:ALCANCE_COLOR[alcance],borderRadius:"6px",transition:"width .3s"}}/>
                    </div>
                  </div>
                );
              })}
              <div style={{marginTop:"12px",padding:"12px",background:oscuro?"#0a1f5c":"#eff6ff",borderRadius:"10px",border:"1px solid #7B5BF240"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"6px"}}>
                  <span style={{fontSize:"13px",fontWeight:"700",color:texto}}>Total bruto</span>
                  <span style={{fontSize:"20px",fontWeight:"800",color:"#7B5BF2"}}>{total.toFixed(3)} tCO₂eq</span>
                </div>
                {totalCompensado>0&&(
                  <>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"6px"}}>
                      <span style={{fontSize:"13px",color:sub}}>Compensaciones</span>
                      <span style={{fontSize:"16px",fontWeight:"700",color:"#22c55e"}}>-{totalCompensado.toFixed(3)} tCO₂eq</span>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",borderTop:`1px solid ${borde}`,paddingTop:"6px"}}>
                      <span style={{fontSize:"13px",fontWeight:"700",color:texto}}>Total neto</span>
                      <span style={{fontSize:"20px",fontWeight:"800",color:"#1C3FAA"}}>{totalNeto.toFixed(3)} tCO₂eq</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div style={card}>
              <h3 style={{fontSize:"14px",fontWeight:"700",color:texto,marginBottom:"12px"}}>Top fuentes de emisión</h3>
              {Object.entries(factores).flatMap(([alcance,fuentes])=>
                fuentes.filter(f=>Number(consumos[f.id]||0)>0).map(f=>{
                  const em = Number(consumos[f.id]||0)*f.factor/1000;
                  const pct = total>0?(em/total*100).toFixed(1):0;
                  return {label:f.label,em,pct,alcance,id:f.id,esAuto:(datos.consumosAuto||{})[f.id]};
                })
              ).sort((a,b)=>b.em-a.em).slice(0,8).map(({label,em,pct,alcance,id,esAuto})=>(
                <div key={id} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`0.5px solid ${borde}`,fontSize:"12px",alignItems:"center"}}>
                  <span style={{color:ALCANCE_COLOR[alcance],fontWeight:"600",minWidth:"32px"}}>[{alcance.toUpperCase()}]</span>
                  <span style={{color:texto,flex:1,marginLeft:"8px"}}>{label}</span>
                  {esAuto && <span style={{fontSize:"9px",fontWeight:"700",background:"#dbeafe",color:"#1d4ed8",padding:"1px 5px",borderRadius:"4px",marginRight:"6px"}}>AUTO</span>}
                  <span style={{color:ALCANCE_COLOR[alcance],fontWeight:"700"}}>{em.toFixed(3)} t · {pct}%</span>
                </div>
              ))}
            </div>

            {/* Benchmark */}
            <div style={{...card,background:oscuro?"#0a1f5c":"#eff6ff",border:"1px solid #3b82f640"}}>
              <h3 style={{fontSize:"13px",fontWeight:"700",color:"#3b82f6",marginBottom:"10px"}}>Comparativa vs benchmark sector gráfico</h3>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"10px",textAlign:"center"}}>
                <div style={{padding:"10px",background:cardBg,borderRadius:"8px",border:`1px solid ${borde}`}}>
                  <p style={{fontSize:"18px",fontWeight:"800",color:"#7B5BF2",margin:"0 0 2px"}}>{(total/Math.max(produccionTon,0.001)).toFixed(3)}</p>
                  <p style={{fontSize:"10px",color:sub,margin:0}}>Tu intensidad (tCO₂eq/t)</p>
                </div>
                <div style={{padding:"10px",background:cardBg,borderRadius:"8px",border:`1px solid ${borde}`}}>
                  <p style={{fontSize:"18px",fontWeight:"800",color:"#6b7280",margin:"0 0 2px"}}>{BENCHMARK_SECTOR}</p>
                  <p style={{fontSize:"10px",color:sub,margin:0}}>Benchmark sector (tCO₂eq/t)</p>
                </div>
                <div style={{padding:"10px",background:cardBg,borderRadius:"8px",border:`1px solid ${score?.color||borde}`,borderWidth:"2px"}}>
                  <p style={{fontSize:"22px",fontWeight:"900",color:score?.color||sub,margin:"0 0 2px"}}>{score?.letra||"—"}</p>
                  <p style={{fontSize:"10px",color:sub,margin:0}}>Score ALQOS™</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── PLAN ── */}
        {tab==='plan'&&(
          <div>
            <div style={{...card,background:oscuro?"#0a1f0a":"#f0fdf4",border:"1px solid #22c55e40"}}>
              <h3 style={{fontSize:"13px",fontWeight:"700",color:"#22c55e",marginBottom:"10px"}}>+ Nueva acción</h3>
              <textarea rows={2} value={formPlan.accion} onChange={e=>setFormPlan(f=>({...f,accion:e.target.value}))} style={{...inp,resize:"none"}} placeholder="Descripción de la acción de reducción..."/>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"8px",marginTop:"8px"}}>
                <div>
                  <span style={lbl}>Alcance</span>
                  <select value={formPlan.alcance} onChange={e=>setFormPlan(f=>({...f,alcance:e.target.value}))} style={inp}>
                    {Object.entries(ALCANCE_LABEL).map(([k,v])=><option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <span style={lbl}>Responsable</span>
                  <input value={formPlan.responsable} onChange={e=>setFormPlan(f=>({...f,responsable:e.target.value}))} style={inp}/>
                </div>
                <div>
                  <span style={lbl}>Reducción est. (t)</span>
                  <input type="number" value={formPlan.reduccionEstimada} onChange={e=>setFormPlan(f=>({...f,reduccionEstimada:e.target.value}))} style={inp}/>
                </div>
              </div>
              <div style={{display:"flex",gap:"8px",marginTop:"8px"}}>
                <div style={{flex:1}}>
                  <span style={lbl}>Fecha compromiso</span>
                  <input type="date" value={formPlan.fechaCompromiso} onChange={e=>setFormPlan(f=>({...f,fechaCompromiso:e.target.value}))} style={inp}/>
                </div>
                <button onClick={agregarAccion} style={{alignSelf:"flex-end",padding:"8px 20px",background:"#22c55e",color:"white",border:"none",borderRadius:"8px",cursor:"pointer",fontSize:"13px",fontWeight:"700"}}>+ Agregar</button>
              </div>
            </div>

            {planAcciones.length===0?(
              <div style={{textAlign:"center",padding:"2rem"}}>
                <p style={{fontSize:"32px"}}>🌱</p>
                <p style={{fontSize:"13px",color:sub}}>Sin acciones. Usá ARIA para generarlas automáticamente.</p>
              </div>
            ):planAcciones.map(a=>(
              <div key={a.id} style={{background:cardBg,borderRadius:"12px",padding:"1rem 1.2rem",border:`1.5px solid ${estadoPlanColor[a.estado]||borde}40`,marginBottom:"8px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",alignItems:"center",gap:"6px",marginBottom:"4px"}}>
                      {a.origenARIA && <span style={{fontSize:"9px",fontWeight:"700",background:"linear-gradient(135deg,#7B5BF2,#09184D)",color:"white",padding:"1px 6px",borderRadius:"4px"}}>ARIA</span>}
                      <p style={{fontSize:"13px",fontWeight:"600",color:texto,margin:0}}>{a.accion}</p>
                    </div>
                    <p style={{fontSize:"11px",color:sub,margin:0}}>{ALCANCE_LABEL[a.alcance]} · {a.responsable||"—"} · {a.fechaCompromiso||"—"}{a.reduccionEstimada?` · Est: ${a.reduccionEstimada} tCO₂eq`:""}</p>
                  </div>
                  <div style={{display:"flex",gap:"6px",alignItems:"center",marginLeft:"10px"}}>
                    <select value={a.estado} onChange={e=>guardar({...datos,planAcciones:planAcciones.map(x=>x.id===a.id?{...x,estado:e.target.value}:x)})}
                      style={{padding:"4px 8px",borderRadius:"6px",border:`1px solid ${estadoPlanColor[a.estado]}`,background:`${estadoPlanColor[a.estado]}15`,color:estadoPlanColor[a.estado],fontSize:"11px",fontWeight:"600",cursor:"pointer"}}>
                      <option value="pendiente">⏳ Pendiente</option>
                      <option value="en_proceso">🔵 En proceso</option>
                      <option value="completado">✅ Completado</option>
                      <option value="cancelado">⛔ Cancelado</option>
                    </select>
                    <button onClick={()=>guardar({...datos,planAcciones:planAcciones.filter(x=>x.id!==a.id)})} style={{background:"none",border:"none",color:"#e63946",cursor:"pointer",fontSize:"16px"}}>×</button>
                  </div>
                </div>
              </div>
            ))}

            {planAcciones.length>0&&(
              <div style={{...card,background:oscuro?"#0a1f0a":"#f0fdf4",border:"1px solid #22c55e40",textAlign:"center"}}>
                <p style={{fontSize:"12px",color:sub,margin:"0 0 4px"}}>Reducción total estimada del plan</p>
                <p style={{fontSize:"24px",fontWeight:"800",color:"#22c55e",margin:"0 0 4px"}}>{planAcciones.reduce((a,p)=>a+(Number(p.reduccionEstimada)||0),0).toFixed(2)} tCO₂eq</p>
                <p style={{fontSize:"11px",color:sub}}>{total>0?((planAcciones.reduce((a,p)=>a+(Number(p.reduccionEstimada)||0),0)/total)*100).toFixed(1):0}% de la huella actual</p>
                <p style={{fontSize:"11px",color:sub,marginTop:"4px"}}>
                  Completadas: {planAcciones.filter(a=>a.estado==='completado').length} / {planAcciones.length}
                </p>
              </div>
            )}
          </div>
        )}

        {/* ── HISTORIAL ── */}
        {tab==='historial'&&(
          <div>
            {historial.length===0?(
              <div style={{textAlign:"center",padding:"3rem"}}>
                <p style={{fontSize:"32px"}}>📅</p>
                <p style={{fontSize:"14px",color:texto}}>Sin historial todavía</p>
                <p style={{fontSize:"12px",color:sub}}>Cuando cierres un período aparecerá acá</p>
              </div>
            ):(
              <>
                <div style={card}>
                  <h3 style={{fontSize:"14px",fontWeight:"700",color:"#7B5BF2",marginBottom:"14px"}}>Evolución anual</h3>
                  {(() => {
                    const todos = [...historial].reverse();
                    const max = Math.max(...todos.map(h=>Number(h.total)));
                    return todos.map((h)=>(
                      <div key={h.id} style={{marginBottom:"10px"}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:"4px"}}>
                          <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                            <span style={{fontSize:"12px",fontWeight:"600",color:texto}}>{h.periodo||h.anio}</span>
                            {h.score && <span style={{fontSize:"10px",fontWeight:"800",padding:"1px 7px",borderRadius:"6px",background:h.score==='A'?"#dcfce7":h.score==='B'?"#dbeafe":h.score==='C'?"#fef9c3":"#fee2e2",color:h.score==='A'?"#15803d":h.score==='B'?"#1d4ed8":h.score==='C'?"#a16207":"#b91c1c"}}>{h.score}</span>}
                          </div>
                          <span style={{fontSize:"12px",fontWeight:"700",color:"#7B5BF2"}}>
                            {Number(h.total).toFixed(3)} tCO₂eq
                            {h.variacion&&<span style={{color:Number(h.variacion)<0?"#22c55e":"#e63946",fontSize:"10px",marginLeft:"6px"}}>({h.variacion}%)</span>}
                          </span>
                        </div>
                        <div style={{background:oscuro?"#091840":"#f4f6fb",borderRadius:"6px",height:"12px",overflow:"hidden"}}>
                          <div style={{width:`${max>0?(Number(h.total)/max*100):0}%`,height:"100%",background:`linear-gradient(90deg,#7B5BF2,#2A7FFF)`,borderRadius:"6px"}}/>
                        </div>
                      </div>
                    ));
                  })()}
                </div>

                {historial.map(h=>(
                  <div key={h.id} style={{...card,borderLeft:"4px solid #7B5BF2"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:"8px"}}>
                      <div>
                        <p style={{fontSize:"14px",fontWeight:"700",color:texto,margin:"0 0 2px"}}>{h.periodo||h.anio}</p>
                        <p style={{fontSize:"11px",color:sub,margin:0}}>Cerrado por {h.cerradoPor||"—"} · {h.fechaCierre?.slice(0,10)}</p>
                      </div>
                      <span style={{fontSize:"20px",fontWeight:"800",color:"#7B5BF2"}}>{Number(h.total).toFixed(3)} t</span>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr",gap:"8px"}}>
                      {[["A1",h.a1,"#e63946"],["A2",h.a2,"#f59e0b"],["A3",h.a3,"#3b82f6"],["Var.",h.variacion?h.variacion+"%":"—",Number(h.variacion)<0?"#22c55e":"#e63946"],["Score",h.score||"—",h.score==='A'?"#22c55e":h.score==='B'?"#3b82f6":h.score==='C'?"#f59e0b":"#e63946"]].map(([k,v,col])=>(
                        <div key={k} style={{textAlign:"center",background:oscuro?"#091840":"#f4f6fb",borderRadius:"8px",padding:"8px"}}>
                          <p style={{fontSize:"13px",fontWeight:"700",color:col,margin:"0 0 2px"}}>{v}</p>
                          <p style={{fontSize:"10px",color:sub,margin:0}}>{k}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* ── FACTORES (solo admin) ── */}
        {tab==='factores' && esAdmin &&(
          <div>
            <div style={{...card,background:oscuro?"#0a1f5c":"#eff6ff",border:"1px solid #3b82f640"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"10px"}}>
                <div>
                  <h3 style={{fontSize:"14px",fontWeight:"700",color:"#3b82f6",margin:"0 0 2px"}}>⚙️ Tabla de factores de emisión</h3>
                  <p style={{fontSize:"11px",color:sub,margin:0}}>Editá los factores base o agregá fuentes personalizadas. Los cambios afectan todos los cálculos del módulo.</p>
                </div>
                <button onClick={resetFactores} style={{padding:"6px 12px",background:"rgba(230,57,70,.1)",border:"1px solid rgba(230,57,70,.3)",borderRadius:"8px",cursor:"pointer",fontSize:"11px",color:"#e63946",fontWeight:"600"}}>
                  🔄 Resetear
                </button>
              </div>
            </div>

            {Object.entries(factores).map(([alcance, fuentes])=>(
              <div key={alcance} style={card}>
                <h3 style={{fontSize:"13px",fontWeight:"700",color:ALCANCE_COLOR[alcance],marginBottom:"12px"}}>{ALCANCE_LABEL[alcance]}</h3>
                {fuentes.map(f=>{
                  const esBase = !!FACTORES_BASE[alcance]?.find(b=>b.id===f.id);
                  const editando = editandoFactor?.alcance===alcance && editandoFactor?.id===f.id;
                  return (
                    <div key={f.id} style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr auto",gap:"8px",alignItems:"center",padding:"8px 10px",borderRadius:"8px",marginBottom:"6px",background:editando?(oscuro?"#0a1830":"#f0f7ff"):(oscuro?"#091840":"#f8fafc"),border:`1px solid ${editando?"#3b82f6":borde}`}}>
                      <div>
                        <p style={{fontSize:"12px",fontWeight:"600",color:texto,margin:"0 0 1px"}}>{f.label}</p>
                        {editando?(
                          <input defaultValue={f.fuente} id={`fuente_${f.id}`}
                            style={{...inp,marginTop:"2px",fontSize:"11px",padding:"4px 8px"}} placeholder="Fuente bibliográfica"/>
                        ):(
                          <p style={{fontSize:"10px",color:sub,margin:0}}>{f.fuente}</p>
                        )}
                      </div>
                      <div style={{textAlign:"center"}}>
                        <p style={{fontSize:"10px",color:sub,margin:"0 0 2px"}}>{f.unidad}</p>
                        {editando?(
                          <input type="number" defaultValue={f.factor} id={`factor_${f.id}`}
                            style={{...inp,marginTop:0,fontSize:"12px",padding:"4px 8px",textAlign:"center"}}/>
                        ):(
                          <p style={{fontSize:"13px",fontWeight:"700",color:ALCANCE_COLOR[alcance],margin:0}}>{f.factor}</p>
                        )}
                      </div>
                      <div style={{textAlign:"center"}}>
                        <p style={{fontSize:"10px",color:sub,margin:"0 0 2px"}}>kgCO₂eq</p>
                        {esBase
                          ? <span style={{fontSize:"9px",fontWeight:"700",background:"#e0f2fe",color:"#0369a1",padding:"1px 6px",borderRadius:"4px"}}>BASE</span>
                          : <span style={{fontSize:"9px",fontWeight:"700",background:"#fef9c3",color:"#a16207",padding:"1px 6px",borderRadius:"4px"}}>CUSTOM</span>
                        }
                      </div>
                      <div style={{display:"flex",gap:"4px"}}>
                        {editando?(
                          <>
                            <button onClick={()=>{
                              const nuevoFactor = document.getElementById(`factor_${f.id}`)?.value;
                              const nuevaFuente = document.getElementById(`fuente_${f.id}`)?.value;
                              guardarFactorEditado(alcance, f.id, nuevoFactor, nuevaFuente||f.fuente);
                            }} style={{padding:"4px 10px",background:"#22c55e",color:"white",border:"none",borderRadius:"6px",cursor:"pointer",fontSize:"11px",fontWeight:"700"}}>✓</button>
                            <button onClick={()=>setEditandoFactor(null)} style={{padding:"4px 8px",background:"none",border:`1px solid ${borde}`,borderRadius:"6px",cursor:"pointer",color:sub,fontSize:"11px"}}>✕</button>
                          </>
                        ):(
                          <>
                            <button onClick={()=>setEditandoFactor({alcance,id:f.id})}
                              style={{padding:"4px 8px",background:"none",border:`1px solid ${borde}`,borderRadius:"6px",cursor:"pointer",color:"#3b82f6",fontSize:"12px"}}>✏️</button>
                            {!esBase && (
                              <button onClick={()=>eliminarFactorCustom(alcance,f.id)}
                                style={{padding:"4px 8px",background:"none",border:"none",cursor:"pointer",color:"#e63946",fontSize:"12px"}}>🗑</button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}

            {/* Agregar factor custom */}
            <div style={{...card,background:oscuro?"#0a1f0a":"#f0fdf4",border:"1px solid #22c55e40"}}>
              <h3 style={{fontSize:"13px",fontWeight:"700",color:"#22c55e",marginBottom:"12px"}}>+ Agregar fuente personalizada</h3>
              <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",gap:"8px"}}>
                <div>
                  <span style={lbl}>Nombre</span>
                  <input value={formNuevoFactor.label} onChange={e=>setFormNuevoFactor(f=>({...f,label:e.target.value}))} style={inp} placeholder="Ej: Solvente especial"/>
                </div>
                <div>
                  <span style={lbl}>Alcance</span>
                  <select value={formNuevoFactor.alcance} onChange={e=>setFormNuevoFactor(f=>({...f,alcance:e.target.value}))} style={inp}>
                    {Object.entries(ALCANCE_LABEL).map(([k,v])=><option key={k} value={k}>{k.toUpperCase()}</option>)}
                  </select>
                </div>
                <div>
                  <span style={lbl}>Unidad</span>
                  <input value={formNuevoFactor.unidad} onChange={e=>setFormNuevoFactor(f=>({...f,unidad:e.target.value}))} style={inp} placeholder="kg"/>
                </div>
                <div>
                  <span style={lbl}>Factor (kgCO₂eq)</span>
                  <input type="number" value={formNuevoFactor.factor} onChange={e=>setFormNuevoFactor(f=>({...f,factor:e.target.value}))} style={inp} placeholder="0.000"/>
                </div>
              </div>
              <div style={{marginTop:"8px"}}>
                <span style={lbl}>Fuente bibliográfica</span>
                <input value={formNuevoFactor.fuente} onChange={e=>setFormNuevoFactor(f=>({...f,fuente:e.target.value}))} style={inp} placeholder="Ej: IPCC AR6 / Ecoinvent v3.9"/>
              </div>
              <button onClick={agregarFactor} style={{marginTop:"12px",padding:"8px 20px",background:"#22c55e",color:"white",border:"none",borderRadius:"8px",cursor:"pointer",fontSize:"13px",fontWeight:"700"}}>+ Agregar factor</button>
            </div>

            <div style={{...card,background:oscuro?"#0a1830":"#f0f7ff",border:"1px solid #3b82f640"}}>
              <p style={{fontSize:"12px",color:sub,margin:0}}>
                💡 <strong style={{color:texto}}>Tip ISO 14064:</strong> Usá siempre la fuente más específica disponible (factor nacional &gt; regional &gt; IPCC). Los factores editados quedan guardados en el sistema y se usan para todos los cálculos futuros.
              </p>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}